import type { SubmitHandler } from 'react-hook-form';
import { useState } from 'react';
import { faFolder } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isArray, isString } from 'lodash';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiUpdateCategoryResponseBody } from '~/lib/api/modules/Categories';
import type { IUseUpdateCategoryMutationValues } from '~/lib/api/modules/Categories/hooks/useUpdateCategoryMutation';
import type { ICategoryFormValues } from './CategoryForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useNavigate } from '~/components/utils/Navigation';
import { useCategoryQuery } from '~/lib/api/modules/Categories/hooks/useCategoryQuery';
import { useUpdateCategoryMutation } from '~/lib/api/modules/Categories/hooks/useUpdateCategoryMutation';
import { CategoryForm } from './CategoryForm';

export type IUpdateCategoryFormProps = IContainerProps & {
  onSuccess?: (
    outputData: IApiUpdateCategoryResponseBody,
    inputValues: IUseUpdateCategoryMutationValues,
  ) => void;
  cancelUrl?: string;
  appId: string;
  categoryId: string;
};

export const UpdateCategoryForm: React.FC<IUpdateCategoryFormProps> = (
  props,
) => {
  const { sx, onSuccess, cancelUrl, appId, categoryId } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const categoryQuery = useCategoryQuery(
    { appId, categoryId },
    {
      onSuccess: (category) => {
        if (category.action) {
          navigate(`/apps/${appId}/links/${categoryId}/update`, {
            replace: true,
          });
        }
      },
    },
  );
  const updateCategoryMutation = useUpdateCategoryMutation();
  const [isDraft, setIsDraft] = useState(false);

  const category = categoryQuery.data;

  const handleSubmit: SubmitHandler<ICategoryFormValues> = (values) => {
    if (updateCategoryMutation.isLoading) {
      return;
    }

    return updateCategoryMutation.mutateAsync(
      {
        appId,
        categoryId,
        body: {
          name: values.name ?? '',
          picture: values.pictures?.length ? [values.pictures[0].id] : [],
          parentId: values.parentId ? values.parentId : undefined,
          action: '',
          hidden: values.isDraft ?? false,
          isEvent: values.isEvent,
          reversedFlow: false,
          badges: values.userBadges,
          badgesAllow: values.requireAllUserBadges ? 'all' : 'any',
          forcedAuthor: values.authorName,
          rssFeedUrl: values.rssFeedUrl,
        },
      },
      { onSuccess },
    );
  };

  return (
    <ContentBlock draft={isDraft} sx={sx} draftOutlineSize="lg">
      <ContentBlock.Title icon={<FontAwesomeIcon icon={faFolder} />}>
        {t('UpdateCategoryForm.title')}{' '}
        {isDraft ? t('UpdateCategoryForm.not-published-yet') : null}
      </ContentBlock.Title>

      <ContentBlock.Suspense
        loading={categoryQuery.isLoading}
        error={categoryQuery.error}
      >
        {category && (
          <CategoryForm
            mode="edition"
            appId={appId}
            categoryId={categoryId}
            cancelUrl={cancelUrl}
            defaultValues={{
              name: category.name,
              pictures: category.picture
                ? isArray(category.picture)
                  ? isString(category.picture[0])
                    ? [{ type: 'picture', id: category.picture[0] }]
                    : undefined
                  : isString(category.picture)
                    ? [{ type: 'picture', id: category.picture }]
                    : undefined
                : undefined,
              parentId: category.parentId ?? '',
              userBadges: category.badges?.list.map(({ id }) => id) ?? [],
              requireAllUserBadges: category.badges?.allow === 'all',
              authorName: category.forcedAuthor ?? '',
              isDraft: !!category.hidden,
              isEvent: category.isEvent,
              rssFeedUrl: category.rssFeedUrl,
            }}
            onSubmit={handleSubmit}
            onDraftStatusChange={setIsDraft}
          />
        )}
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
